import {
  IPaymentGateway,
  IPaymentMethodResp,
  PaymentMethodStatusEnum,
  PaymentMethodTypeEnum,
} from '@monetize/types/app';
import { FilterTypeOperator } from '@monetize/types/common';
import { FunctionComponent as FC, useEffect, useState } from 'react';
import { usePaymentMethodsData } from '../../../hooks/AccountDetail';
import useCurrentPaymentGateways from '../../../hooks/useCurrentPaymentGateways';
import { useFlags } from '../../../services/launchDarkly';
import {
  MBox,
  MButton,
  MCenterModal,
  MCustomRadioGroup,
  MGrid,
  MGridItem,
  MStack,
} from '../../Monetize';
import { ImportPaymentMethodModal } from './ImportPaymentMethodModal';
import { NewStripePaymentMethodModal } from './NewStripePaymentMethodModal';
import { NewWireTransferPaymentMethodModal } from './NewWireTransferPaymentMethodModal';

import { ACH_OR_BANK_TRANSFER } from '../../../constants/paymentMethods';

interface AddPaymentMethodFormProps {
  isOpen: boolean;
  onClose: (paymentMethod?: IPaymentMethodResp) => void;
  accountId: string;
}

export const AddPaymentMethodForm: FC<AddPaymentMethodFormProps> = ({
  isOpen,
  onClose,
  accountId,
}: AddPaymentMethodFormProps) => {
  const [value, setValue] = useState<
    'import' | 'stripe' | 'wire-transfer' | ''
  >('');
  const [valueConfirmed, setValueConfirmed] = useState(false);

  const { enableAddWireTransferPaymentMethod } = useFlags();

  const {
    setFilters: setGetPaymentMethodsFilters,
    totalRecords: numberOfActiveAchOrBankTransferMethods,
    isFetching: paymentMethodsDataFetching,
  } = usePaymentMethodsData(accountId, { enabled: isOpen });

  const { currentPaymentGateway, isFetching: paymentGatewayLoading } =
    useCurrentPaymentGateways({
      enabled: isOpen,
      refetchOnWindowFocus: true,
    });

  const hasAchOrBankTransferEnabled = (
    paymentGateway: IPaymentGateway,
  ): boolean =>
    paymentGateway.allowedPaymentMethods.some(
      ({ enabled, method }) => enabled && ACH_OR_BANK_TRANSFER.has(method),
    );

  useEffect(() => {
    setGetPaymentMethodsFilters([
      {
        key: 'paymentMethodType',

        operator: FilterTypeOperator.IN,

        value: [
          PaymentMethodTypeEnum.ACH_CREDIT,
          PaymentMethodTypeEnum.US_BANK_TRANSFER,
        ],
      },
      {
        key: 'status',
        operator: FilterTypeOperator.EQUAL,
        value: PaymentMethodStatusEnum.ACTIVE,
      },
    ]);
  }, [setGetPaymentMethodsFilters]);

  const allOptions = [
    {
      title: 'Add New Payment Method',
      value: 'stripe',
      description: 'For new customers',
      condition: true, // Always show
    },
    {
      title: 'Import Payment Method',
      value: 'import',
      description: 'For customers on Stripe with existing payment methods',
      condition: true, // Always show
    },
    {
      title: 'Create Wire Transfer Payment Method',
      value: 'wire-transfer',
      description: 'Create Stripe Bank Transfer payment method',
      condition:
        enableAddWireTransferPaymentMethod &&
        numberOfActiveAchOrBankTransferMethods === 0 &&
        currentPaymentGateway &&
        hasAchOrBankTransferEnabled(currentPaymentGateway) &&
        !paymentMethodsDataFetching,
    },
  ];

  const OPTIONS = allOptions
    .filter((option) => option.condition)
    .map(({ condition, ...option }) => option);
  const onContinue = () => {
    setValueConfirmed(true);
  };

  // The form stays mounted while closed (e.g. inside the Pay Invoice modal), so
  // reopening it skipped the options and went straight back to the last choice.
  const handleClose = (paymentMethod?: IPaymentMethodResp) => {
    setValue('');
    setValueConfirmed(false);
    onClose(paymentMethod);
  };

  return (
    <>
      {!valueConfirmed && isOpen && (
        <MCenterModal
          isOpen={isOpen}
          onClose={handleClose}
          modalTitle="Add Electronic Payment Method"
          size="md"
          isLoading={paymentGatewayLoading || paymentMethodsDataFetching}
          modalContentProps={{ 'data-testid': 'add-method-form' } as any}
          renderFooter={() => (
            <MStack
              gap={4}
              direction="row"
              align="center"
              justify="right"
              flex={1}
            >
              <MButton
                onClick={() => handleClose()}
                variant="cancel"
                minW="auto"
              >
                Cancel
              </MButton>
              <MButton
                variant="primary"
                isDisabled={!value}
                onClick={onContinue}
                minW="auto"
              >
                Continue
              </MButton>
            </MStack>
          )}
        >
          <MBox>
            <MGrid templateColumns="repeat(12, 1fr)" gap={4} mb={3}>
              <MGridItem colSpan={12}>
                <MCustomRadioGroup
                  value={value}
                  onChange={(val) => setValue(val as any)}
                  itemDescription="description"
                  items={OPTIONS}
                />
              </MGridItem>
            </MGrid>
          </MBox>
        </MCenterModal>
      )}
      {valueConfirmed && value === 'import' && isOpen && (
        <ImportPaymentMethodModal
          isOpen={isOpen}
          onClose={handleClose}
          accountId={accountId}
        />
      )}
      {valueConfirmed && value === 'stripe' && isOpen && (
        <NewStripePaymentMethodModal
          isOpen={isOpen}
          onClose={handleClose}
          accountId={accountId}
        />
      )}
      {valueConfirmed &&
        value === 'wire-transfer' &&
        currentPaymentGateway &&
        isOpen && (
          <NewWireTransferPaymentMethodModal
            isOpen={isOpen}
            onClose={handleClose}
            accountId={accountId}
            paymentGatewayId={currentPaymentGateway.id}
          />
        )}
    </>
  );
};
